/**
 * Grid Pagination Utilities
 *
 * Handles pagination logic for StandardDataGrid
 */

/**
 * Convert pagination model to backend format
 * @param {Object} paginationModel - MUI pagination model { page, pageSize }
 * @returns {Object} Backend pagination params { offset, limit }
 */
export const convertPaginationModelToBackend = (paginationModel) => {
  const { page = 0, pageSize = 25 } = paginationModel || {};

  return {
    offset: page * pageSize,
    limit: pageSize
  };
};

/**
 * Convert backend pagination params to MUI pagination model
 * @param {Object} params - Backend params { offset, limit }
 * @returns {Object} MUI pagination model { page, pageSize }
 */
export const convertBackendToPaginationModel = (params) => {
  const { offset = 0, limit = 25 } = params || {};

  return {
    page: limit > 0 ? Math.floor(offset / limit) : 0,
    pageSize: limit
  };
};

/**
 * Create pagination change handler for MUI v5
 * @param {Function} setPaginationModel - State setter for pagination model
 * @param {Function} saveGridState - Function to save grid state to storage
 * @param {Function} onPaginationChange - Callback when pagination changes
 * @returns {Object} Handlers for page and pageSize changes
 */
export const createPaginationHandlers = (setPaginationModel, saveGridState, onPaginationChange) => {
  const handlePageChange = (newPage) => {
    setPaginationModel((prev) => {
      const newModel = { ...prev, page: newPage };
      saveGridState({ paginationModel: newModel });
      if (onPaginationChange) {
        onPaginationChange(newModel);
      }
      return newModel;
    });
  };

  const handlePageSizeChange = (newPageSize) => {
    setPaginationModel(() => {
      const newModel = { page: 0, pageSize: newPageSize }; // Reset to first page
      saveGridState({ paginationModel: newModel });
      if (onPaginationChange) {
        onPaginationChange(newModel);
      }
      return newModel;
    });
  };

  return {
    handlePageChange,
    handlePageSizeChange
  };
};

/**
 * Get default pagination model
 * @param {number} defaultPageSize - Default page size (default: 25)
 * @returns {Object} Default pagination model { page: 0, pageSize }
 */
export const getDefaultPaginationModel = (defaultPageSize = 25) => {
  return {
    page: 0,
    pageSize: defaultPageSize
  };
};

/**
 * Validate pagination model
 * @param {Object} paginationModel - Pagination model to validate
 * @returns {boolean} True if valid
 */
export const isValidPaginationModel = (paginationModel) => {
  if (!paginationModel || typeof paginationModel !== 'object') {
    return false;
  }

  const { page, pageSize } = paginationModel;

  return (
    typeof page === 'number' &&
    typeof pageSize === 'number' &&
    page >= 0 &&
    pageSize > 0
  );
};
